import { extendTheme } from "@chakra-ui/react";
import {
  colors,
  KEEPS_OWN_FOCUS_LINE_ATTRIBUTE,
  ON_JUNGLE_APP_PAGE,
  ON_JUNGLE_DARK_PAGE,
} from "./constants/GlobalStyles";

type FieldStyleProps = { focusBorderColor?: string; errorBorderColor?: string };

// Jungle's dark colors belong to a learner's page in the dark theme. The admin area has a dark mode of its
// own, which is plain Chakra and must stay as it is (src/theme/jungleColorTheme.ts). Chakra's `_dark`
// would match both, so the dark styles below are keyed on ON_JUNGLE_DARK_PAGE, the attribute only a
// learner's dark page carries. That selector also outranks Chakra's own dark styles for the same element.

// Chakra validates `focusBorderColor` / `errorBorderColor` with a color parser and falls back to black for
// anything it cannot parse, which includes every theme color (they are CSS variables). Put the value back
// as written.
//
// On a learner's page every field that shows focus shows it the same way, in the brand green, in both
// themes: the focus colors the fields were built with are a pale grey (hard to tell from the line of a
// field at rest, and gone on a dark surface) or Chakra's default blue (the only blue in the product). A
// field whose focus color is the surface color asks for no visible change on focus, and keeps that. So
// does a field that carries KEEPS_OWN_FOCUS_LINE (GlobalStyles.ts says when a field should).
//
// A field with an error keeps its red line while it has focus: that is when the person is correcting it.
// Without the second rule the focus color, written later than the error color, would cover it.
function keepThemeColorOnFocusAndInvalidRing(
  buildRing: (ringColor: string) => Record<string, string>
): (props: FieldStyleProps) => Record<string, unknown> {
  const isThemeColor = (color: string | undefined): color is string =>
    typeof color === "string" && color.startsWith("var(");
  return ({ focusBorderColor, errorBorderColor }) => ({
    field: {
      ...(isThemeColor(focusBorderColor) ? { _focusVisible: buildRing(focusBorderColor) } : {}),
      ...(isThemeColor(errorBorderColor) ? { _invalid: buildRing(errorBorderColor) } : {}),
      ...(focusBorderColor !== colors.surface.white
        ? {
            [`${ON_JUNGLE_APP_PAGE}:not([${KEEPS_OWN_FOCUS_LINE_ATTRIBUTE}])`]: {
              _focusVisible: buildRing(colors.primaryAction.outlineBorder),
              "&[aria-invalid=true]:focus-visible, &[data-invalid]:focus-visible": buildRing(
                isThemeColor(errorBorderColor) ? errorBorderColor : colors.accentColors.errorStrong
              ),
            },
          }
        : {}),
    },
  });
}

const fieldRingForOutlineAndFilledVariants = keepThemeColorOnFocusAndInvalidRing((ringColor) => ({
  borderColor: ringColor,
  boxShadow: `0 0 0 1px ${ringColor}`,
}));
const fieldRingForFlushedVariant = keepThemeColorOnFocusAndInvalidRing((ringColor) => ({
  borderColor: ringColor,
  boxShadow: `0px 1px 0px 0px ${ringColor}`,
}));
const fieldVariantsThatKeepThemeColors = {
  outline: fieldRingForOutlineAndFilledVariants,
  filled: fieldRingForOutlineAndFilledVariants,
  flushed: fieldRingForFlushedVariant,
};

// Textarea is a single-part component: its variant returns the field's style directly.
function unwrapFieldStyle(buildFieldVariant: (props: FieldStyleProps) => Record<string, unknown>) {
  return (props: FieldStyleProps) => buildFieldVariant(props).field as Record<string, unknown>;
}
const textareaVariantsThatKeepThemeColors = {
  outline: unwrapFieldStyle(fieldRingForOutlineAndFilledVariants),
  filled: unwrapFieldStyle(fieldRingForOutlineAndFilledVariants),
  flushed: unwrapFieldStyle(fieldRingForFlushedVariant),
};

// ── defaults that a component's own props still override ───────────────────────────────────────────────
// A rule keyed on ON_JUNGLE_DARK_PAGE is a nested selector: it outweighs the `bg` or `color` a component
// passes as a prop, whatever the order (a first version of the rule below turned every solid button on a
// dark page grey, because Jungle's own Button is a Chakra button that passes its colors as props). A
// DEFAULT must therefore be an ordinary declaration, which a prop replaces. So it is written as a variable
// with Chakra's own value as the fallback: `var(--jungle-default-…, <what Chakra would have used>)`. The
// variables are set on a dark learner page only (src/theme/jungleThemeColors.css, "defaults for Chakra's
// own parts"); everywhere else the fallback applies and nothing changes.
function chakraColorVariable(colorToken: string): string {
  return `var(--chakra-colors-${colorToken.replace(".", "-")})`;
}

// A plain Chakra button (grey, no colors of its own). In its dark mode Chakra fills it with white at a low
// alpha: a neutral grey with no hue, on pages whose greys all carry the theme's tint. On a dark learner
// page it takes the theme's own greys. The fallbacks are Chakra's values for the same states (button.mjs
// in @chakra-ui/theme), in its light or its dark mode.
function greyButtonWithJungleGreysOnDarkPage(variant: "solid" | "outline" | "ghost") {
  return ({ colorScheme, colorMode }: { colorScheme?: string; colorMode?: string }) => {
    if (colorScheme !== "gray") return {};
    const chakraGrey = (lightModeToken: string, darkModeToken: string) =>
      chakraColorVariable(colorMode === "dark" ? darkModeToken : lightModeToken);
    if (variant === "solid") {
      const fillAtRest = `var(--jungle-default-grey-button-fill, ${chakraGrey("gray.100", "whiteAlpha.200")})`;
      return {
        bg: fillAtRest,
        _hover: {
          bg: `var(--jungle-default-grey-button-fill-hover, ${chakraGrey("gray.200", "whiteAlpha.300")})`,
          _disabled: { bg: fillAtRest },
        },
        _active: { bg: `var(--jungle-default-grey-button-fill-active, ${chakraGrey("gray.300", "whiteAlpha.400")})` },
      };
    }
    return {
      ...(variant === "outline"
        ? { borderColor: `var(--jungle-default-grey-button-line, ${chakraGrey("gray.200", "whiteAlpha.300")})` }
        : {}),
      _hover: { bg: `var(--jungle-default-grey-button-hover, ${chakraGrey("gray.100", "whiteAlpha.200")})` },
      _active: { bg: `var(--jungle-default-grey-button-active, ${chakraGrey("gray.200", "whiteAlpha.300")})` },
    };
  };
}

// Chakra names a toast's color by status: green (success), red (error), orange (warning), blue (info).
const TOAST_FILL_ON_JUNGLE_DARK_PAGE_BY_COLOR_SCHEME: Record<string, string> = {
  green: colors.primaryAction.fill,
  red: colors.accentColors.errorFill,
  orange: "#a16207",
  blue: "#2563a8",
};

// The Chakra theme shared by the app (App.tsx) and the marketing site entry (marketing-entry.tsx), so a
// component looks the same wherever it renders. Overrides beyond fonts remove the mobile tap highlight
// and give Chakra's own surfaces (modal, menu, popover, …) Jungle's colors on a dark learner page; every
// such value is a theme color, so nothing here changes the light theme. Chakra's own page, text and
// border colors for that page are set in src/theme/jungleThemeColors.css.
//
// `initialColorMode` is the theme the page starts in (src/theme/jungleColorTheme.ts decides it). Chakra
// renders its first frame with it, before it has asked the color mode manager.
//
// `hasDarkLearnerPages` is false for the marketing site, which is always light. A rule for a component is
// written into the HTML of every page that renders the component, and a marketing page's HTML has a size
// budget (checkMarketingSeo.mjs): the rules for a plain grey button on a dark page would be dead weight
// on every marketing page with a button, so that site leaves them out.
export function createJungleChakraTheme(
  fonts: { body: string } = { body: "Outfit, sans-serif" },
  initialColorMode: "light" | "dark" = "light",
  hasDarkLearnerPages = true,
  direction: "ltr" | "rtl" = "ltr"
) {
  return extendTheme({
    direction,
    config: {
      initialColorMode,
      useSystemColorMode: false,
    },
    fonts,
    styles: {
      global: {
        // Remove tap highlight on all elements
        "*": {
          WebkitTapHighlightColor: "transparent",
        },
        // Specific overrides for interactive elements
        "button, a, input, textarea, select": {
          WebkitTapHighlightColor: "transparent",
        },
        // Remove focus outline on mobile while keeping accessibility
        "button:focus, a:focus": {
          outline: "none",
          boxShadow: "none",
        },
        // Ensure no blue highlight on active state
        "button:active, a:active": {
          WebkitTapHighlightColor: "transparent",
        },
      },
    },
    components: {
      Button: {
        baseStyle: {
          WebkitTapHighlightColor: "transparent",
          _focus: {
            boxShadow: "none",
          },
          _active: {
            WebkitTapHighlightColor: "transparent",
          },
        },
        ...(hasDarkLearnerPages
          ? {
              variants: {
                solid: greyButtonWithJungleGreysOnDarkPage("solid"),
                outline: greyButtonWithJungleGreysOnDarkPage("outline"),
                ghost: greyButtonWithJungleGreysOnDarkPage("ghost"),
              },
            }
          : {}),
      },
      Input: { variants: fieldVariantsThatKeepThemeColors },
      NumberInput: { variants: fieldVariantsThatKeepThemeColors },
      PinInput: { variants: fieldVariantsThatKeepThemeColors },
      Select: { variants: fieldVariantsThatKeepThemeColors },
      Textarea: { variants: textareaVariantsThatKeepThemeColors },
      Modal: {
        baseStyle: {
          dialog: { [ON_JUNGLE_DARK_PAGE]: { "--modal-bg": colors.surface.white } },
          // The veil behind a dialog. Chakra's own (black at 48%) is enough over a plain dark page, but over
          // a picture (the river on home) it leaves the picture brighter than the dialog in front of it. On a
          // dark page every dialog takes the theme's scrim; an overlay that names its color keeps it.
          overlay: { bg: `var(--jungle-default-dialog-scrim, ${chakraColorVariable("blackAlpha.600")})` },
          // Chakra's default text is as dark as a heading on the light theme. On a dark page it is the
          // body color, so a dialog's title is given the heading color there. A title that names its
          // color keeps it.
          header: { color: "var(--jungle-default-dialog-title-color, inherit)" },
          // The container fills the window and can receive focus. The browser's own focus line around it is
          // a blue hairline round the whole window that tells nobody anything, on either theme.
          dialogContainer: { [ON_JUNGLE_APP_PAGE]: { outline: "none" } },
        },
      },
      Drawer: {
        baseStyle: {
          dialog: { [ON_JUNGLE_DARK_PAGE]: { "--drawer-bg": colors.surface.white } },
          overlay: { bg: `var(--jungle-default-dialog-scrim, ${chakraColorVariable("blackAlpha.600")})` },
        },
      },
      Menu: {
        baseStyle: {
          list: { [ON_JUNGLE_DARK_PAGE]: { "--menu-bg": colors.surface.white, borderColor: colors.surface.border } },
          item: {
            [ON_JUNGLE_DARK_PAGE]: {
              color: colors.text.body,
              "&:focus, &[data-focus]": { "--menu-bg": colors.surface.hover },
              "&:active, &[data-active]": { "--menu-bg": colors.surface.focus },
              "&[aria-expanded=true], &[data-expanded]": { "--menu-bg": colors.surface.hover },
            },
          },
        },
      },
      Popover: {
        baseStyle: {
          content: {
            [ON_JUNGLE_DARK_PAGE]: {
              "--popper-bg": colors.surface.white,
              "--popper-arrow-shadow-color": colors.surface.border,
            },
          },
        },
      },
      Tooltip: {
        baseStyle: {
          [ON_JUNGLE_DARK_PAGE]: { "--tooltip-bg": colors.surface.inverse, "--tooltip-fg": colors.text.white },
        },
      },
      Skeleton: {
        baseStyle: {
          [ON_JUNGLE_DARK_PAGE]: {
            "--skeleton-start-color": colors.surface.secondary,
            "--skeleton-end-color": colors.surface.tertiary,
          },
        },
      },
      // A ticked box. Chakra's dark tick box is a pale pastel of its color scheme; on a dark learner page it is
      // the theme's own green (the color of a switch that is on), with a dark tick.
      Checkbox: {
        baseStyle: {
          control: {
            [ON_JUNGLE_DARK_PAGE]: {
              "&[data-checked]:not([data-disabled])": {
                background: colors.primaryAction.default,
                borderColor: colors.primaryAction.default,
                color: colors.surface.offWhite,
              },
              "&[data-checked]:not([data-disabled])[data-hover]": {
                background: colors.primaryAction.hover,
                borderColor: colors.primaryAction.hover,
              },
            },
          },
        },
      },
      Card: {
        variants: {
          elevated: { container: { [ON_JUNGLE_DARK_PAGE]: { "--card-bg": colors.surface.white } } },
        },
      },
      // Toasts. Chakra's dark toast is a pale pastel box with dark text, which glares on a dark page; a
      // dark learner page gets a solid fill with a white message, as the light theme has.
      Alert: {
        variants: {
          solid: ({ colorScheme }: { colorScheme?: string }) => ({
            container: {
              [ON_JUNGLE_DARK_PAGE]: {
                "--alert-fg": colors.text.white,
                "--alert-bg":
                  TOAST_FILL_ON_JUNGLE_DARK_PAGE_BY_COLOR_SCHEME[colorScheme ?? ""] ?? colors.secondaryAction.fill,
              },
            },
          }),
        },
      },
    },
  });
}
